/**
 * A pane of glass coming apart, as numbers.
 *
 * The pane is cut into a grid of shards once, at load, and every shard is a
 * pure function of one number: how long ago the window broke. There is no
 * emitter, no pool, and nothing that integrates a velocity frame by frame -
 * ask where shard `i` is 0.4 s in and you get the same answer on every
 * machine, at every frame rate, however long the tab spent in the background.
 *
 * That is the same trick `Plume.ts` uses and it is here for a sharper reason.
 * The window is broken in the first two seconds of a run, and a run is
 * restarted more often than it is finished: anything that accumulated would
 * have to be torn down and rebuilt on every `R`, and anything integrated would
 * drift between the machine that recorded a replay and the machine that plays
 * it back. A closed-form shard cannot do either. Resetting it is assigning
 * `null` to one field.
 *
 * At age zero the shards tile the pane exactly, which is what lets `World`
 * swap the whole sheet for the pieces on a single frame with nothing visible
 * happening. Everything after that is ballistics and a fade.
 *
 * No three.js in here: `World` owns the meshes, this owns where they go. Same
 * split as Plume and BeerSwing, and for the same reason - it can be sampled
 * ten thousand times in a test with no canvas anywhere near it.
 */

import { SHATTER } from '../config/gameplay';
import type { PaneImpact } from '../world/Ikkuna';
import type { Box, IkkunaSpec } from '../world/Level';

/** One shard's fixed identity. Authored once, at load, never stepped. */
export interface ShardSeed {
  /** Which cell of the pane it was cut from. */
  column: number;
  row: number;
  /** Sideways drift and initial lift, metres per second. Signed. */
  driftX: number;
  lift: number;
  /** Tumble about each axis, radians per second. Signed. */
  spinX: number;
  spinY: number;
  spinZ: number;
}

/** Where a shard is this frame, and how much of it is left. */
export interface ShardPose {
  x: number;
  y: number;
  z: number;
  /** Metres, across and up. The pane's own cell, minus `SHATTER.fill`. */
  width: number;
  height: number;
  rotationX: number;
  rotationY: number;
  rotationZ: number;
  /** 0..1. Zero once it has been lying in the grass for `SHATTER.settle`. */
  opacity: number;
}

export function shardPose(): ShardPose {
  return {
    x: 0,
    y: 0,
    z: 0,
    width: 0,
    height: 0,
    rotationX: 0,
    rotationY: 0,
    rotationZ: 0,
    opacity: 0,
  };
}

/**
 * Cuts a pane into `SHATTER.columns * SHATTER.rows` shards.
 *
 * `rng` is expected to be a seeded generator called at load time and never
 * again - the same allowance src/utils/prng.ts is written for. Nothing about a
 * shard is random once the level is built, which is why two players watching
 * the same replay watch the same window break.
 */
export function shardSeeds(rng: () => number): ShardSeed[] {
  const seeds: ShardSeed[] = [];
  for (let row = 0; row < SHATTER.rows; row++) {
    for (let column = 0; column < SHATTER.columns; column++) {
      seeds.push({
        column,
        row,
        driftX: (rng() - 0.5) * 2 * SHATTER.spread,
        // Never downward: a pane that is pushed through drops out of its
        // frame, and a shard thrown at the sill reads as one that bounced off
        // something that is not there.
        lift: rng() * SHATTER.lift,
        spinX: (rng() - 0.5) * 2 * SHATTER.spin,
        spinY: (rng() - 0.5) * 2 * SHATTER.spin,
        spinZ: (rng() - 0.5) * 2 * SHATTER.spin,
      });
    }
  }
  return seeds;
}

/** The middle of shard `seed`'s cell, and how big that cell is. */
function cell(pane: Box, seed: ShardSeed): { x: number; y: number; width: number; height: number } {
  const width = (pane.maxX - pane.minX) / SHATTER.columns;
  const height = (pane.maxY - pane.minY) / SHATTER.rows;
  return {
    x: pane.minX + (seed.column + 0.5) * width,
    y: pane.minY + (seed.row + 0.5) * height,
    width,
    height,
  };
}

/**
 * Shard `seed` of `spec`'s pane, `age` seconds after the glass went, into
 * `out`.
 *
 * Written into a caller-owned pose rather than returned, because this runs
 * once per shard per frame and ninety-six allocations a frame is a window that
 * shows up in a profile for no reason at all.
 */
export function sampleShard(
  spec: IkkunaSpec,
  seed: ShardSeed,
  impact: PaneImpact,
  age: number,
  out: ShardPose,
): ShardPose {
  const pane = spec.pane;
  const home = cell(pane, seed);
  const z = (pane.minZ + pane.maxZ) / 2;

  out.width = home.width * SHATTER.fill;
  out.height = home.height * SHATTER.fill;

  // How near this shard was to the hole the runner made, 1 at it and 0 well
  // away from it. Measured in the pane's own fractions rather than in metres,
  // so a wider window does not change how the break spreads.
  const u = (home.x - pane.minX) / (pane.maxX - pane.minX);
  const v = (home.y - pane.minY) / (pane.maxY - pane.minY);
  const distance = Math.hypot(u - impact.u, v - impact.v);
  const nearness = Math.max(0, 1 - distance / SHATTER.impactReach);

  // The glass at the impact lets go first; the corners hang on. That is the
  // whole difference between a pane breaking and a pane being deleted.
  const released = age - (1 - nearness) * SHATTER.delay;
  if (released <= 0) {
    out.x = home.x;
    out.y = home.y;
    out.z = z;
    out.rotationX = 0;
    out.rotationY = 0;
    out.rotationZ = 0;
    out.opacity = 1;
    return out;
  }

  const push = SHATTER.push + nearness * SHATTER.impactPush;
  const rest = spec.ground + out.height / 2;

  // When it reaches the grass. Solved rather than stepped: y(t) is a parabola,
  // and asking a parabola where it crosses a line is arithmetic. Everything
  // else freezes at the same `t`, so a shard stops sliding when it lands
  // instead of skating off across the takapiha.
  const drop = home.y - rest;
  const landing =
    drop <= 0
      ? 0
      : (seed.lift + Math.sqrt(seed.lift * seed.lift + 2 * SHATTER.gravity * drop)) /
        SHATTER.gravity;
  const t = Math.min(released, landing);

  out.x = home.x + seed.driftX * t;
  out.y = home.y + seed.lift * t - 0.5 * SHATTER.gravity * t * t;
  out.z = z + spec.outward * push * t;
  out.rotationX = seed.spinX * t;
  out.rotationY = seed.spinY * t;
  out.rotationZ = seed.spinZ * t;

  // Down, and going out. Not because glass evaporates, but because what is
  // actually there afterwards is a scatter of chips in long grass, and ninety-
  // six lit quads lying in it read as litter rather than as glass.
  const lying = released - landing;
  out.opacity = lying <= 0 ? 1 : Math.max(0, 1 - lying / SHATTER.settle);
  return out;
}

/**
 * Seconds from the break to the last shard going out. Nothing draws after it.
 *
 * An upper bound rather than the exact figure, and deliberately: it is the
 * slowest shard there could be - one at the top of the pane, released last,
 * thrown as hard upward as a seed allows - taken from the top of the glass to
 * the ground rather than to where a shard of its size actually comes to rest.
 * Everything real is quicker, and a bound that is a hair long is the only kind
 * that is safe to draw conclusions from.
 */
export function shatterDuration(spec: IkkunaSpec): number {
  const drop = Math.max(0, spec.pane.maxY - spec.ground);
  // The same solved parabola `sampleShard` uses, with the lift at its limit.
  const fall =
    (SHATTER.lift + Math.sqrt(SHATTER.lift * SHATTER.lift + 2 * SHATTER.gravity * drop)) /
    SHATTER.gravity;
  return SHATTER.delay + fall + SHATTER.settle;
}
